import { useState } from "react";
import { Newspaper, MapPin, Calendar } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal, SectionLabel } from "@/components/site/primitives";
import { Lightbox } from "@/components/site/Lightbox";
import { MEDIA } from "@/data/content";

const ClippingCard = ({ item, onOpen }) => (
  <Reveal>
    <article
      data-testid={`media-item-${item.id}`}
      className="group h-full flex flex-col border border-raritan-border bg-raritan-surface hover:shadow-sm transition-shadow"
    >
      <button
        type="button"
        onClick={onOpen}
        className="relative w-full aspect-[4/5] overflow-hidden bg-white cursor-zoom-in border-b border-raritan-border"
      >
        <img
          src={item.image}
          alt={item.title}
          className="w-full h-full object-contain p-3"
        />
        <span className="absolute top-3 left-3 bg-raritan-red px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-white whitespace-nowrap">
          {item.publication}
        </span>
        <span className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-raritan-ink whitespace-nowrap">
          {item.id}
        </span>
      </button>
      <div className="p-5 md:p-6 flex-1 flex flex-col">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-raritan-red">{item.tag}</span>
        <h3 className="mt-2 font-heading font-bold text-lg md:text-xl tracking-tight text-raritan-ink leading-snug">{item.title}</h3>
        <p className="mt-3 font-body text-sm text-raritan-muted leading-relaxed flex-1">{item.summary}</p>
        <div className="mt-4 pt-4 border-t border-raritan-border flex flex-wrap gap-x-4 gap-y-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-raritan-muted">
          <span className="flex items-center gap-1.5"><MapPin size={11} className="text-raritan-red" />{item.location}</span>
          <span className="flex items-center gap-1.5"><Calendar size={11} className="text-raritan-red" />{item.period}</span>
        </div>
      </div>
    </article>
  </Reveal>
);

export default function Media() {
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const lightboxItems = MEDIA.map((m) => ({ src: m.image, caption: `${m.title} — ${m.publication}, ${m.period}` }));

  return (
    <div data-testid="media-page">
      <PageHero
        label="Raritan in Media"
        titleLines={["Stories from", "the field,", "in print"]}
        intro="Selected press coverage of Raritan Consulting and the Karmanya Foundation — ward-level workshops, plantation drives, health camps, and city-wide sanitation rallies."
        testId="media-hero"
      />

      {/* Clippings grid */}
      <section className="px-5 md:px-10 py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
            <div>
              <SectionLabel className="mb-5"><Newspaper size={12} className="text-raritan-red" /> Press archive</SectionLabel>
              <h2 className="font-heading font-black uppercase tracking-tighter text-3xl md:text-5xl text-raritan-ink leading-[0.95]">
                All clippings,<br />in one place
              </h2>
            </div>
            <p className="max-w-sm font-body text-sm text-raritan-muted leading-relaxed">
              Click any clipping to open the full-size scan and read the article uncropped.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
            {MEDIA.map((m, i) => (
              <ClippingCard key={m.id} item={m} onOpen={() => setLightboxIdx(i)} />
            ))}
          </div>
        </div>
      </section>

      <Lightbox
        items={lightboxItems}
        index={lightboxIdx}
        onClose={() => setLightboxIdx(-1)}
        onIndexChange={setLightboxIdx}
      />
    </div>
  );
}
